import { useTaskFields } from "@jugl-web/domain-resources/tasks/hooks/useTaskFields";
import { useTranslations } from "@jugl-web/utils";
import { useLocalStorage } from "@jugl-web/utils/hooks/useStorage";
import { TASK_CUSTOM_FIELD_COLUMNS_BY_ENTITY_ID_KEY } from "@jugl-web/utils/storage";
import { FC, ReactNode, createContext, useCallback, useMemo } from "react";
import { TABLE_DIMENSIONS } from "../../consts";
import { TaskTableColumn } from "../../types";
import { AssigneeCell } from "../AssigneeCell";
import { ChecklistCell } from "../ChecklistCell";
import { ChecklistItemAssigneeCell } from "../ChecklistItemAssigneeCell";
import { ChecklistItemDueDateCell } from "../ChecklistItemDueDateCell";
import { ChecklistItemNameCell } from "../ChecklistItemNameCell";
import { CustomFieldCell } from "../CustomFieldCell";
import { DueDateCell } from "../DueDateCell";
import { LabelCell } from "../LabelCell";
import { PriorityCell } from "../PriorityCell";
import { StatusCell } from "../StatusCell";
import { TaskNameCell } from "../TaskNameCell";
import { TaskTableLayout } from "../TaskTableLayout";

type CustomFieldColumnsMap = Record<string, boolean>;
type CustomFieldColumnsByEntityId = Record<string, CustomFieldColumnsMap>;

interface TaskTableColumnsContextValue {
  columns: TaskTableColumn[];
  customFieldColumnsMap: CustomFieldColumnsMap;
  changeCustomFieldColumnsMap: (
    updatedColumnsMap: CustomFieldColumnsMap
  ) => void;
}

export const TaskTableColumnsContext =
  createContext<TaskTableColumnsContextValue | null>(null);

interface TaskTableColumnsProviderProps {
  entityId: string;
  children: ReactNode;
}

export const TaskTableColumnsProvider: FC<TaskTableColumnsProviderProps> = ({
  entityId,
  children,
}) => {
  const { customFields } = useTaskFields({ entityId });

  const [customFieldColumnsByEntityId, setCustomFieldColumnsByEntityId] =
    useLocalStorage<CustomFieldColumnsByEntityId>(
      TASK_CUSTOM_FIELD_COLUMNS_BY_ENTITY_ID_KEY,
      {}
    );

  const defaultCustomFieldColumnsMap = useMemo<CustomFieldColumnsMap>(
    () =>
      customFields.reduce<CustomFieldColumnsMap>((acc, customField) => {
        acc[customField.id] = false;
        return acc;
      }, {}),
    [customFields]
  );

  const customFieldColumnsMap = useMemo(
    () =>
      customFieldColumnsByEntityId[entityId] || defaultCustomFieldColumnsMap,
    [customFieldColumnsByEntityId, defaultCustomFieldColumnsMap, entityId]
  );

  const customFieldBasedColumns = useMemo<TaskTableColumn[]>(() => {
    const activeCustomFields = customFields.filter(
      (customField) => !!customFieldColumnsMap[customField.id]
    );

    return activeCustomFields.map((customField) => ({
      id: customField.id,
      title: customField.name,
      width: TABLE_DIMENSIONS.TABLE_CUSTOM_FIELD_COLUMN_WIDTH,
      taskCellRenderer: (props) => (
        <CustomFieldCell field={customField} {...props} />
      ),
      checklistCellRenderer: () => null,
    }));
  }, [customFieldColumnsMap, customFields]);

  const { t } = useTranslations();

  const changeCustomFieldColumnsMap = useCallback(
    (updatedColumnsMap: CustomFieldColumnsMap) => {
      setCustomFieldColumnsByEntityId((prevState) => ({
        ...prevState,
        [entityId]: updatedColumnsMap,
      }));
    },
    [entityId, setCustomFieldColumnsByEntityId]
  );

  const columns = useMemo<TaskTableColumn[]>(
    () => [
      {
        id: "task",
        title: t({
          id: "tasks-page.task-table-header",
          defaultMessage: "Task",
        }),
        width: TABLE_DIMENSIONS.TABLE_TASK_TITLE_COLUMN_WIDTH,
        taskCellRenderer: (props) => <TaskNameCell {...props} />,
        checklistCellRenderer: (props) => <ChecklistItemNameCell {...props} />,
      },
      {
        id: "due_date",
        title: t({
          id: "tasks-page.due-date-table-header",
          defaultMessage: "Due Date",
        }),
        width: TABLE_DIMENSIONS.TABLE_DUE_DATE_COLUMN_WIDTH,
        taskCellRenderer: (props) => <DueDateCell {...props} />,
        checklistCellRenderer: (props) => (
          <ChecklistItemDueDateCell {...props} />
        ),
      },
      {
        id: "assignee",
        title: t({
          id: "tasks-page.assignee-table-header",
          defaultMessage: "Assignee",
        }),
        width: TABLE_DIMENSIONS.TABLE_ASSIGNEE_COLUMN_WIDTH,
        taskCellRenderer: (props) => <AssigneeCell {...props} />,
        checklistCellRenderer: (props) => (
          <ChecklistItemAssigneeCell {...props} />
        ),
      },
      {
        id: "checklist",
        title: t({
          id: "tasks-page.checklist-table-header",
          defaultMessage: "Checklist",
        }),
        width: TABLE_DIMENSIONS.TABLE_CHECKLIST_COLUMN_WIDTH,
        taskCellRenderer: (props) => <ChecklistCell {...props} />,
        checklistCellRenderer: ({ cellProps }) => (
          <TaskTableLayout.Cell {...cellProps} shouldGrow />
        ),
      },
      {
        id: "priority",
        title: t({
          id: "tasks-page.priority-table-header",
          defaultMessage: "Priority",
        }),
        width: TABLE_DIMENSIONS.TABLE_PRIORITY_COLUMN_WIDTH,
        taskCellRenderer: (props) => <PriorityCell {...props} />,
        checklistCellRenderer: () => null,
      },
      {
        id: "label",
        title: t({
          id: "tasks-page.label-table-header",
          defaultMessage: "Label",
        }),
        width: TABLE_DIMENSIONS.TABLE_LABEL_COLUMN_WIDTH,
        taskCellRenderer: (props) => <LabelCell {...props} />,
        checklistCellRenderer: () => null,
      },
      {
        id: "status",
        title: t({
          id: "tasks-page.status-table-header",
          defaultMessage: "Status",
        }),
        width: TABLE_DIMENSIONS.TABLE_STATUS_COLUMN_WIDTH,
        taskCellRenderer: (props) => <StatusCell {...props} />,
        checklistCellRenderer: () => null,
      },
      ...customFieldBasedColumns,
    ],
    [customFieldBasedColumns, t]
  );

  const contextValue = useMemo(
    () => ({
      columns,
      customFieldColumnsMap,
      changeCustomFieldColumnsMap,
    }),
    [changeCustomFieldColumnsMap, columns, customFieldColumnsMap]
  );

  return (
    <TaskTableColumnsContext.Provider value={contextValue}>
      {children}
    </TaskTableColumnsContext.Provider>
  );
};
